import logger from 'packages/logger';

/**
 * Description:
 * module with helper functions to create GPT objects, preferably used in combination with html data attributes as below
 * 
 *
 * Sample of how to define a new ad slot:
 *
    <adslot
        data-g-ad-format="panorama,wide_skyscraper"   // separate multiple formats with a comma
        data-g-ad-device="desktop"                    // mobile,tablet,desktop
        data-g-ad-position="top-desktop" />             // match this towards the "pos" in DFP
 *
 */

/**
 * @returns array of arrays of 2 elements with [width, height]:
 * @param {string} adFormat - accepts format value of:  panorama, wide_skyscraper, half_page, medium_rectangle, mobile_rectangle
 */
const mapSingleAdFormatToSizes = adFormat => {
    const viewPortMapping = {
        panorama: [[980, 240], [980, 160], [980, 120], [970, 250], 'fluid'],
        wide_skyscraper: [[160, 600]],
        half_page: [
            [300, 600],
            [250, 600]
        ],
        medium_rectangle: [[300, 250]],
        mobile_rectangle: [[320, 320]],
        mobile_wideboard: [[320, 160]]
    };
    return viewPortMapping[adFormat];
};

/**
 * @returns array of arrays of 2 elements with slot [width, height]:
 * @param {string} adFormats - accepts one or more ',' separated format values
 */
const mapManyAdFormatsToSizes = adFormats => {
    return adFormats
        .split(',')
        .map(adFormat => mapSingleAdFormatToSizes(adFormat))
        .reduce((pre, cur) => pre.concat(cur));
};

/**
 * @param {string} device - accepts one of: mobile, tablet, desktop, desktop-lg
 * @returns array with first elements: [min width, max width]
 */
const mapDeviceNameToViewport = device => {
    let deviceViewportMappings = {
        mobile: [0, 449],
        tablet: [450, 1249],
        desktop: [1250, 0],
        'desktop-lg': [1600, 0]
    };
    return deviceViewportMappings[device];
};

/**
 * @param {string} adFormats - accepts one or more ',' separated format values
 * @param {string} device - accepts one of: mobile, tablet, desktop, desktop-lg
 */
const createGptSizeMapping = (adFormats, device) => {
    if (!adFormats || !device) {
        logger(
            'Missing some of the required arguments: adFormats, device',
            true
        );
    }

    let [minWidth, maxWidth] = mapDeviceNameToViewport(device);
    let sizes = mapManyAdFormatsToSizes(adFormats);

    let sizeMap = [
        {
            viewport: [minWidth, 0],
            slot: sizes
        }
    ];

    if (maxWidth > 0) {
        sizeMap.push({
            viewport: [maxWidth + 1, 0], // add 1 pixel to make max-width inclusive
            slot: []
        });
    }

    if (0 < minWidth) {
        sizeMap.push({
            viewport: [0, 0],
            slot: []
        });
    }

    return sizeMap;
};

export {
    createGptSizeMapping,
    mapSingleAdFormatToSizes,
    mapManyAdFormatsToSizes,
    mapDeviceNameToViewport
};
